"use client";

import { type ReactNode, useEffect, useRef } from "react";

import { cn } from "../../utils";
import {
  NumericInputActionButtons,
  NumericInputBase,
  type NumericInputBaseProps,
  NumericInputIcon,
  NumericInputLabel,
  NumericInputMessage,
} from "./components";

export type NumericInputVariant = "primary" | "secondary";

export interface NumericInputProps extends NumericInputBaseProps {
  label?: ReactNode;
  error?: ReactNode;
  warning?: ReactNode;
  variant?: NumericInputVariant;
  autoFocus?: boolean | unknown[];
  rightLabel?: ReactNode;
  glowEffect?: boolean;
  labelClassName?: string;
  rightLabelClassName?: string;
  wrapperClassName?: string;
  extraInfo?: ReactNode;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  rightSlot?: ReactNode;
  minButtonValue?: number | string;
  maxButtonValue?: number | string;
}

export const NumericInput: React.FC<NumericInputProps> = ({
  ref,
  disabled,
  className,
  autoFocus = false,
  label,
  error,
  warning,
  variant = "primary",
  rightLabel,
  glowEffect = false,
  labelClassName,
  rightLabelClassName,
  wrapperClassName,
  extraInfo,
  leftIcon,
  rightIcon,
  rightSlot,
  minButtonValue,
  maxButtonValue,
  onChange,
  ...props
}) => {
  const internalRef = useRef<HTMLInputElement>(null);
  const inputRef = (ref ?? internalRef) as React.RefObject<HTMLInputElement>;

  const hasLabel = Boolean(label);
  const isPrimary = variant === "primary";
  const isSecondary = variant === "secondary";
  const hasMessage = Boolean(error || warning || extraInfo);
  const autoFocusDependencies = Array.isArray(autoFocus) ? autoFocus : [];

  useEffect(() => {
    if (!autoFocusDependencies.length) return;

    inputRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we need this to be re-run when the autoFocus dependencies change
  }, [...autoFocusDependencies, inputRef]);

  useEffect(() => {
    if (!autoFocus || typeof autoFocus !== "boolean") return;

    inputRef.current?.focus();
  }, [autoFocus, inputRef]);

  const handleMouseUp = (event: React.MouseEvent<HTMLDivElement>) => {
    const inputElement = typeof inputRef === "object" ? inputRef.current : null;

    if (!inputElement || event.target === inputElement) return;

    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  return (
    <div
      role="button"
      tabIndex={-1}
      className={cn("ui:flex ui:flex-col", {
        "ui:gap-1": isPrimary || hasMessage,
      })}
      onMouseUp={handleMouseUp}
    >
      {isPrimary && (
        <NumericInputLabel
          label={label}
          variant="primary"
          rightLabel={rightLabel}
          labelClassName={labelClassName}
          rightLabelClassName={rightLabelClassName}
        />
      )}

      <div
        className={cn(
          "ui:flex ui:items-center ui:gap-2 ui:rounded-sm ui:border ui:border-divider ui:pr-2 ui:pl-3 ui:transition-colors",
          {
            "ui:h-11 ui:min-h-11": isPrimary,
            "ui:h-8 ui:min-h-8 ui:justify-between": isSecondary,
            "ui:border-error": error,
            "ui:border-warning": warning,
            "ui:hover:border-primary ui:has-focus:border-primary":
              !error && !warning && !disabled,
            "ui:border-primary ui:shadow-[0px_1px_6px_0px_rgba(53,80,252,0.50)] ui:dark:shadow-[0px_1px_6px_0px_var(--ui-color-primary)]":
              glowEffect && !disabled,
          },
          wrapperClassName,
        )}
      >
        {isSecondary && (
          <NumericInputLabel
            label={label}
            variant="secondary"
            labelClassName={labelClassName}
          />
        )}

        <NumericInputIcon icon={leftIcon} position="left" />

        <NumericInputBase
          ref={inputRef}
          disabled={disabled}
          className={cn(
            "ui:my-2 ui:flex-1 ui:border-none ui:text-textPrimary ui:outline-0 ui:placeholder:text-textSecondary",
            {
              "ui:text-xs": isSecondary,
              "ui:text-right": isSecondary && hasLabel,
            },
            className,
          )}
          onChange={onChange}
          {...props}
        />

        {isPrimary && (
          <NumericInputActionButtons
            maxButtonValue={maxButtonValue}
            minButtonValue={minButtonValue}
            onChange={onChange}
          />
        )}

        <NumericInputIcon icon={rightIcon} position="right" />

        {rightSlot}
      </div>

      <NumericInputMessage
        error={error}
        warning={warning}
        extraInfo={extraInfo}
      />
    </div>
  );
};
